import { ImageBlock, DocumentBlock } from '../components/Pleri/store/chatStore';
import { FileUploadResult } from '../api/file-upload';
import {
  getImageMimeTypes,
  getTextMimeTypes,
  getMaxFileSize,
  isSupportedFileType,
} from '../constants';

// File size limits (in bytes) - keeping for backward compatibility
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// Supported file types - using new helper functions
export const SUPPORTED_IMAGE_TYPES = getImageMimeTypes();
export const SUPPORTED_DOCUMENT_TYPES = getTextMimeTypes();

type SupportedDocumentType = string;

/**
 * Type guard to check if a string is a supported document type
 */
const isSupportedDocumentType = (
  type: string,
): type is SupportedDocumentType => {
  return SUPPORTED_DOCUMENT_TYPES.includes(type);
};

export const ALL_SUPPORTED_TYPES = [
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_DOCUMENT_TYPES,
];

export interface FileProcessingResult {
  success: boolean;
  error?: string;
  contentBlock?: ImageBlock | DocumentBlock;
}

export interface FileUploadProcessingResult {
  success: boolean;
  error?: string;
  contentBlock?: ImageBlock | DocumentBlock;
  uploadResult?: FileUploadResult;
  /** Workspace-relative path (e.g. "uploads/data.csv") — set when file was uploaded to a conversation workspace. */
  workspacePath?: string;
}

/**
 * Validates if a file is supported and within size limits
 */
export const validateFile = (
  file: File,
): { valid: boolean; error?: string } => {
  // Check file type
  if (!isSupportedFileType(file.type) && !!file.type) {
    return {
      valid: false,
      error: `Unsupported file type. Supported types: Images (JPEG, PNG, WebP), Documents (PDF), and Text files (TXT, MD, CSV, JSON, XML, YAML, and programming languages)`,
    };
  }

  // Check file size using new helper function
  const maxSize = getMaxFileSize(file.type);
  if (file.size > maxSize) {
    const maxSizeMB = maxSize / (1024 * 1024);
    return {
      valid: false,
      error: `File too large. Maximum size: ${maxSizeMB}MB`,
    };
  }

  return { valid: true };
};

/**
 * Converts a file to base64 string
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Processes a file and returns appropriate content block
 */
export const processFile = async (
  file: File,
): Promise<FileProcessingResult> => {
  // Validate file first
  const validation = validateFile(file);
  if (!validation.valid) {
    return {
      success: false,
      error: validation.error,
    };
  }

  try {
    const base64Data = await fileToBase64(file);

    if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      // Create ImageBlock
      const imageBlock: ImageBlock = {
        type: 'image',
        source: {
          type: 'base64',
          media_type: file.type as
            | 'image/jpeg'
            | 'image/png'
            | 'image/gif'
            | 'image/webp',
          data: base64Data,
        },
      };
      return {
        success: true,
        contentBlock: imageBlock,
      };
    } else if (isSupportedDocumentType(file.type) || !file.type) {
      // Create DocumentBlock
      const documentBlock: DocumentBlock = {
        type: 'document',
        source: {
          type: 'base64',
          media_type: file.type || 'text/plain', // Type is narrowed by the type guard
          data: base64Data,
        },
        name: file.name,
        size: file.size,
      };
      return {
        success: true,
        contentBlock: documentBlock,
      };
    }

    return {
      success: false,
      error: 'Unsupported file type',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to process file',
    };
  }
};

/**
 * Formats file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Processes a file using the upload API and returns appropriate content block with URL
 */
export const processFileWithUpload = async (
  file: File,
  uploadFn: (
    file: File,
    onProgress?: (progress: {
      loaded: number;
      total: number;
      percentage: number;
    }) => void,
  ) => Promise<FileUploadResult>,
  onProgress?: (progress: {
    loaded: number;
    total: number;
    percentage: number;
  }) => void,
): Promise<FileUploadProcessingResult> => {
  // Validate file first
  const validation = validateFile(file);
  if (!validation.valid) {
    return {
      success: false,
      error: validation.error,
    };
  }

  try {
    // Upload file to cloud storage
    const uploadResult = await uploadFn(file, onProgress);

    if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
      // Create ImageBlock with URL
      const imageBlock: ImageBlock = {
        type: 'image',
        source: {
          type: 'url',
          media_type: file.type as
            | 'image/jpeg'
            | 'image/png'
            | 'image/gif'
            | 'image/webp',
          url: uploadResult.fileUrl,
        },
      };
      return {
        success: true,
        contentBlock: imageBlock,
        uploadResult,
        workspacePath: uploadResult.workspacePath,
      };
    } else if (isSupportedDocumentType(file.type) || !file.type) {
      // Create DocumentBlock with URL
      const documentBlock: DocumentBlock = {
        type: 'document',
        source: {
          type: 'url',
          media_type: file.type, // Type is narrowed by the type guard
          url: uploadResult.fileUrl,
        },
        name: file.name,
        size: file.size,
      };
      return {
        success: true,
        contentBlock: documentBlock,
        uploadResult,
        workspacePath: uploadResult.workspacePath,
      };
    }

    return {
      success: false,
      error: 'Unsupported file type',
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload file',
    };
  }
};

/**
 * Gets file type category for display purposes
 */
export const getFileTypeCategory = (
  mimeType: string,
): 'image' | 'document' | 'unknown' => {
  if (SUPPORTED_IMAGE_TYPES.includes(mimeType)) {
    return 'image';
  }
  if (isSupportedDocumentType(mimeType)) {
    return 'document';
  }
  return 'unknown';
};
